import { Injectable } from '@angular/core';
import { waitSeconds } from '@reflact/tsutil';
import { SendMailRequest, SendMailResponse } from '@shared/index';
import { mailSendSchema, mailWebhookSchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { EventManagerService } from '../event-manager/event-manager.service';
import { UserMailService } from '../user-mail.service';
import { WebhookManagerService } from '../webhook-manager.service';

/**
 * Versendet E-Mails direkt oder über einen Webhook (`RAGAI.MAIL`).
 * @remarks
 * {@link send} verschickt eine E-Mail direkt über den internen Mailversand (`UserMailService`); {@link webhook}
 * leitet stattdessen an einen extern konfigurierten Webhook eines Skills weiter (über `RAGAI.WEBHOOK.call()`) -
 * nützlich, um E-Mail-Inhalte an ein externes System (z. B. CRM, Ticket-System) statt an einen echten
 * E-Mail-Server zu senden.
 */
@Injectable({
  providedIn: 'root'
})
export class MailManagerService {

  constructor(private webhookManager: WebhookManagerService, private eventManager: EventManagerService, private userMailService: UserMailService) { }
  /**
   * Baut ein E-Mail-Umschlag-Objekt (Empfänger, Betreff, Inhalt).
   * @param to Empfänger-Adresse.
   * @param subject Betreff der E-Mail.
   * @param body Inhalt der E-Mail.
   * @returns Das Umschlag-Objekt.
   */
  public async getEnvelope(to: string, subject: string, body: string) {
    await waitSeconds(0);
    return { to, subject, body };

  }
  /**
   * Sendet eine E-Mail direkt über den konfigurierten Mailversand.
   * @param request Die E-Mail-Daten, oder ein Objekt gemäß `mailSendSchema`.
   * @returns Das Ergebnis des Versands.
   */
  public async send(request: SendMailRequest | z.input<typeof mailSendSchema>): Promise<SendMailResponse> {
    const parsed = mailSendSchema.safeParse(request);
    if (parsed.success && typeof request === 'object' && !('to' in request && 'subject' in request && 'body' in request)) {
      return await this.send(parsed.data);
    }
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.MAIL.SEND.send', { request });
    const result = await this.userMailService.sendMail(request);
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.MAIL.SEND.done', { request, result });
    return result;
  }
  /**
   * Sendet eine E-Mail über einen konfigurierten Webhook.
   * @param webhook ID des Webhooks, oder ein Objekt gemäß `mailWebhookSchema`.
   * @param envelope Empfänger, Betreff und Inhalt der E-Mail.
   * @param envelope.to Empfänger-Adresse.
   * @param envelope.subject Betreff der E-Mail.
   * @param envelope.body Inhalt der E-Mail.
   * @returns Nichts.
   */
  public async webhook(webhook: string | z.input<typeof mailWebhookSchema>, envelope?: { to: string; subject: string; body: string }): Promise<void> {
    const parsed = mailWebhookSchema.safeParse(webhook);
    if (parsed.success && typeof webhook === 'object') {
      envelope = parsed.data.envelope as typeof envelope ?? envelope;
      webhook = parsed.data.webhook;
    }
    await waitSeconds(0);
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.MAIL.WEBHOOK.send', { webhook, envelope });
    const result = await this.webhookManager.call(webhook as string, envelope);
    this.eventManager._trigger_RAGAI_EVENT('RAGAI.MAIL.WEBHOOK.done', { webhook, envelope, result });
  }
}
